/* Source-faithful centered hero pair: measured native artwork bounds and bounded forward depth. */
(function () {
  'use strict';
  const GAP = 14, EDGE = 14;
  const ratios = { shake: 923 / 280, flake: 529 / 431 };
  const idleLimits = { bob:11, tilt:1, pointerX:4.4, pointerY:3.2 };
  const radians = value => value * Math.PI / 180;
  // CSS consumes the existing controller's progress; this helper makes the
  // layer relationship and measured title-travel limit independently testable.
  function layerState(progress, wordmarkTop, mobile = false) {
    const p = Math.max(0, Math.min(1, progress));
    const titleTravel = Math.max(0, Math.min(96, wordmarkTop - (mobile ? 80 : 96)));
    return { titleTravel, titleY:-titleTravel*p, titleScale:1-.12*p,
      coastY:42*p, coastScale:1.08-.06*p };
  }
  // Mirrors the CSS transform, right-to-left, at every scroll position. The
  // source image is unchanged; tight boxes remove old transparent layout gutters.
  function project(kind, width, height, progress, idle = {}) {
    const shake = kind === 'shake';
    // Tip both caps toward the center as depth increases, and unwind exactly
    // along the same path on up-scroll. Keep this in sync with CSS rotateZ.
    const rz = radians(shake ? -12 + 24 * progress : 11 - 22 * progress);
    const ry = radians(shake ? -2 + 4 * progress : 2 - 4 * progress);
    const rx = radians((shake ? 3 : -2) * progress);
    const points = [], tilt = radians(idle.tilt || 0);
    for (const cornerX of [-width / 2, width / 2]) for (const cornerY of [-height / 2, height / 2]) {
      // Pointer translation sits inside the animated inner layer. Its rotation
      // therefore acts on both the source corner and pointer offset before bob.
      const u = cornerX + (idle.pointerX || 0), v = cornerY + (idle.pointerY || 0);
      const x = u * Math.cos(tilt) - v * Math.sin(tilt);
      const y = u * Math.sin(tilt) + v * Math.cos(tilt) + (idle.bob || 0);
      const x1 = x * Math.cos(rz) - y * Math.sin(rz), y1 = x * Math.sin(rz) + y * Math.cos(rz);
      const x2 = x1 * Math.cos(ry), z2 = -x1 * Math.sin(ry);
      const y2 = y1 * Math.cos(rx) - z2 * Math.sin(rx), z3 = y1 * Math.sin(rx) + z2 * Math.cos(rx);
      const scale = 1200 / (1200 - z3 - (shake ? 190 : 215) * progress);
      points.push({ x:(x2 + (shake ? -8 : 8) * progress) * scale, y:(y2 - 18 * progress) * scale });
    }
    return { left:Math.min(...points.map(p => p.x)), right:Math.max(...points.map(p => p.x)), top:Math.min(...points.map(p => p.y)), bottom:Math.max(...points.map(p => p.y)) };
  }
  function envelope(kind, width, height) {
    const angle = radians(idleLimits.tilt), cos = Math.cos(angle), sin = Math.sin(angle);
    const halfWidth = width / 2 + idleLimits.pointerX, halfHeight = height / 2 + idleLimits.pointerY;
    // Enclose every independent +/-1 degree inner tilt, shared cursor offset,
    // and 0..-11px bob BEFORE the outer perspective/scroll transform. All native
    // bottle ratios put the small-angle extrema at the rotation endpoints.
    const outerWidth = 2 * (halfWidth * cos + halfHeight * sin);
    const outerHeight = 2 * (halfHeight * cos + halfWidth * sin) + idleLimits.bob;
    const rest = [], moving = [];
    for (let i = 0; i <= 100; i++) {
      rest.push(project(kind, width, height, i / 100));
      moving.push(project(kind, outerWidth, outerHeight, i / 100, { bob:-idleLimits.bob / 2 }));
    }
    // Keep a 12px minimum reserve and expand wherever the combined approach
    // and idle motion need more room. 0.75px covers scroll sampling and pixel rounding.
    return { left:Math.min(Math.min(...rest.map(p=>p.left))-12, Math.min(...moving.map(p=>p.left))-.75),
      right:Math.max(Math.max(...rest.map(p=>p.right))+12, Math.max(...moving.map(p=>p.right))+.75),
      top:Math.min(Math.min(...rest.map(p=>p.top))-12, Math.min(...moving.map(p=>p.top))-.75),
      bottom:Math.max(Math.max(...rest.map(p=>p.bottom))+12, Math.max(...moving.map(p=>p.bottom))+.75) };
  }
  // Reserve space for the inward cap turn at maximum depth, including each
  // bottle's independent idle tilt. The width solver keeps this inside mobile edges.
  const restGap = (height, mobile) => Math.max(mobile ? 22 : 30, height * .105);
  function solve(layout) {
    const originalShake = layout.products.find(p=>p.kind==='shake');
    const initialHeight = originalShake.height;
    function sized(height) {
      return ['shake','flake'].map(kind => {
        const h = kind==='shake' ? height : height*529/923, width=h/ratios[kind];
        return { kind, width, height:h, rest:project(kind,width,h,0), envelope:envelope(kind,width,h) };
      });
    }
    let products=sized(initialHeight);
    function requiredWidth(pair) {
      const [shake,flake] = pair, gap = restGap(shake.height,layout.mobile);
      const restWidth = shake.rest.right-shake.rest.left+gap+flake.rest.right-flake.rest.left;
      const shakeCenter = -restWidth/2-shake.rest.left;
      const flakeCenter = shakeCenter+shake.rest.right+gap-flake.rest.left;
      // Reserve the actual outer edges of the centered composition, not the
      // sum of two independently padded boxes. This avoids needless shrinking.
      return 2*Math.max(-shakeCenter-shake.envelope.left,flakeCenter+flake.envelope.right);
    }
    if(requiredWidth(products)>layout.width-2*EDGE) {
      let low=0, high=initialHeight;
      for(let i=0;i<16;i++) {
        const height=(low+high)/2, candidate=sized(height);
        if(requiredWidth(candidate)<=layout.width-2*EDGE) low=height; else high=height;
      }
      products=sized(low);
    }
    const gap=restGap(products[0].height,layout.mobile);
    const [shake,flake]=products;
    const pairWidth=shake.rest.right-shake.rest.left+gap+flake.rest.right-flake.rest.left;
    const pairLeft=(layout.width-pairWidth)/2;
    shake.cx=pairLeft-shake.rest.left;
    flake.cx=shake.cx+shake.rest.right+gap-flake.rest.left;
    // Keep the entire title readable through the existing approach and idle
    // motion. Only the pair's common vertical anchor changes, never its size,
    // spacing or transform path. Reserve the closest title/bottle encounter.
    const titleGap = -Math.min(layout.mobile ? 48 : 120, layout.wordmark.height * .36);
    const a=radians(idleLimits.tilt), hw=shake.width/2+idleLimits.pointerX, hh=shake.height/2+idleLimits.pointerY;
    const motionWidth=2*(hw*Math.cos(a)+hh*Math.sin(a));
    const motionHeight=2*(hh*Math.cos(a)+hw*Math.sin(a))+idleLimits.bob;
    shake.cy=-Infinity;
    for(let i=0;i<=100;i++){
      const progress=i/100, layer=layerState(progress,layout.wordmark.top,layout.mobile);
      const titleBottom=layout.wordmark.top+layer.titleY+layout.wordmark.height*layer.titleScale;
      const moving=project('shake',motionWidth,motionHeight,progress,{bob:-idleLimits.bob/2});
      shake.cy=Math.max(shake.cy,titleBottom+titleGap-moving.top+.75);
    }
    flake.cy=shake.cy+(shake.height-flake.height)/2;
    // The existing stage/CTA extension below preserves room under the pair.
    const groupDrop=Math.max(0,...products.map(p=>EDGE-p.cy-p.envelope.top));
    products=products.map(p=>({kind:p.kind,width:p.width,height:p.height,left:p.cx-p.width/2,top:p.cy+groupDrop-p.height/2,
      bounds:{left:p.cx+p.envelope.left,right:p.cx+p.envelope.right,top:p.cy+groupDrop+p.envelope.top,bottom:p.cy+groupDrop+p.envelope.bottom}}));
    const ctaTop=Math.min(...layout.ctaRects.map(r=>r.top));
    const extra=Math.max(0,...products.map(p=>p.bounds.bottom+GAP-ctaTop));
    const stageHeight=Math.ceil((layout.height+extra)*10)/10;
    const reasons=[];
    for(const p of products){
      if(![p.left,p.top,p.width,p.height,stageHeight].every(Number.isFinite)) reasons.push('The measured layout is incomplete.');
      if(p.bounds.left<0||p.bounds.right>layout.width||p.bounds.bottom>stageHeight) reasons.push(p.kind+' would cross the stage edge.');
      for(const rect of layout.ctaRects) if(p.bounds.bottom+8>rect.top+stageHeight-layout.height&&p.bounds.right+8>rect.left&&p.bounds.left-8<rect.right) reasons.push(p.kind+' would obscure the CTA.');
    }
    return {products,stageHeight,extraHeight:stageHeight-layout.height,groupDrop,center:layout.width/2,fits:reasons.length===0,reasons};
  }
  if (typeof module === 'object' && module.exports) { module.exports = { solve, project, envelope, ratios, idleLimits, restGap, layerState }; return; }
  if (typeof document === 'undefined') return;
  const properties = ['stage-height','title-travel','wordmark-top','shake-top','shake-left','shake-width','shake-height','flake-top','flake-left','flake-width','flake-height','base-bottom'];
  let dispose = null;
  function start() {
    if (dispose) return;
    const stage = document.querySelector('body.dm-home .dm-hero-stage');
    if (!stage) return;
    const wordmark = stage.querySelector('.dm-hero-wordmark'), base = stage.querySelector('.dm-hero-base');
    const image = wordmark && wordmark.querySelector('img'), tagline = base && base.querySelector('p'), button = base && base.querySelector('.dm-pill');
    const floats = ['shake','flake'].map(kind => ({ kind, node: stage.querySelector('.dm-float--' + kind) }));
    if (!image || !tagline || !button || floats.some(item => !item.node)) return;
    let frame = 0, timer = 0, stopped = false;
    function measure() {
      frame = 0;
      if (stopped || !stage.isConnected) return;
      stage.removeAttribute('data-pb-readable-hero');
      try {
        if (!stage.clientWidth || !stage.clientHeight || !image.complete) return;
        const origin = stage.getBoundingClientRect(), range = document.createRange();
        range.selectNodeContents(tagline);
        const rects = [range.getBoundingClientRect(), button.getBoundingClientRect()].map(rect => ({ left: rect.left-origin.left, right: rect.right-origin.left, top: rect.top-origin.top, bottom: rect.bottom-origin.top }));
        const products = floats.map(item => {
          const style = getComputedStyle(item.node);
          return { kind:item.kind, width:parseFloat(style.width), height:parseFloat(style.height), left:parseFloat(style.left), top:parseFloat(style.top) };
        });
        const layout = { width:stage.clientWidth, height:stage.clientHeight, mobile:window.matchMedia('(max-width: 760px)').matches, products, wordmark:{ top:wordmark.offsetTop, height:Math.min(image.clientHeight,image.clientWidth*160/420) }, ctaRects:rects };
        const result = solve(layout);
        // Never apply a known-failing placement. Leave the original layout and
        // a diagnostic error instead of marking a collision state as ready.
        if (!result.fits) throw new Error(result.reasons.join(' '));
        const values = { 'stage-height':result.stageHeight, 'title-travel':layerState(1,layout.wordmark.top,layout.mobile).titleTravel, 'wordmark-top':layout.wordmark.top, 'base-bottom':parseFloat(getComputedStyle(base).bottom) };
        for (const product of result.products) {
          values[product.kind+'-top'] = product.top; values[product.kind+'-height'] = product.height;
          values[product.kind+'-left'] = product.left; values[product.kind+'-width'] = product.width;
        }
        for (const name of properties) stage.style.setProperty('--pb-readable-'+name, values[name].toFixed(3)+'px');
        stage.setAttribute('data-pb-readable-hero','ready');
        // A lower pair must remain inside its scroll section on short screens.
        stage.parentElement.style.setProperty('--pb-hero-min-scroll-height',result.stageHeight+'px');
      } catch (error) { console.warn('Pink Bird hero placement could not be applied.', error); }
    }
    function schedule() { if (!stopped && !frame) frame = requestAnimationFrame(measure); }
    function resized() { clearTimeout(timer); timer = setTimeout(schedule,100); }
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(resized) : null;
    // Observe width/font-sensitive children, not the stage height we write.
    if (observer) { observer.observe(image); observer.observe(base); }
    window.addEventListener('resize',resized,{passive:true}); image.addEventListener('load',schedule);
    if (document.fonts) { document.fonts.ready.then(schedule); if (document.fonts.addEventListener) document.fonts.addEventListener('loadingdone',schedule); }
    dispose = function () {
      stopped = true; clearTimeout(timer); if (frame) cancelAnimationFrame(frame); if (observer) observer.disconnect();
      window.removeEventListener('resize',resized); image.removeEventListener('load',schedule);
      if (document.fonts && document.fonts.removeEventListener) document.fonts.removeEventListener('loadingdone',schedule);
      stage.parentElement.style.removeProperty('--pb-hero-min-scroll-height');
      stage.removeAttribute('data-pb-readable-hero'); for (const name of properties) stage.style.removeProperty('--pb-readable-'+name); dispose = null;
    };
    schedule();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',start,{once:true}); else start();
  window.addEventListener('pagehide',() => { if (dispose) dispose(); }); window.addEventListener('pageshow',start);
})();
